import { skipToken, useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  Shipment,
  ListShipmentsInput,
  ListShipmentsResponse,
  GetShipmentResponse,
} from '@erp/rpc';

export function useShipmentsList(filters: ListShipmentsInput = {}) {
  const query = useQuery(
    orpc.supplyChain.shipments.list.queryOptions({ input: filters }),
  );

  const shipments = query.data?.shipments ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    shipments,
    pagination,
  } satisfies typeof query & {
    data?: ListShipmentsResponse;
    shipments: Shipment[];
    pagination: ListShipmentsResponse['pagination'] | undefined;
  };
}

export function useShipment(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...(id
      ? orpc.supplyChain.shipments.get.queryOptions({ input: { id } })
      : { queryKey: ['shipment', 'get', 'disabled'], queryFn: skipToken }),
    enabled: Boolean(id) && enabled,
    // ShipmentSchema uses z.coerce.date() for createdAt/updatedAt. React Query's
    // replaceEqualDeep treats Date as opaque, so each refetch produces a new
    // `data` reference and cascades re-renders across observers. Without a
    // stale window, this triggers an infinite refetch loop on pages that
    // render multiple shipment observers (e.g. AP invoice detail).
    staleTime: 30_000,
  });

  return {
    ...query,
    shipment: query.data?.shipment ?? null,
    salesOrders: query.data?.salesOrders ?? [],
    purchaseOrders: query.data?.purchaseOrders ?? [],
    chargesCount: query.data?.chargesCount ?? 0,
  } satisfies typeof query & {
    data?: GetShipmentResponse;
    shipment: GetShipmentResponse['shipment'] | null;
    salesOrders: GetShipmentResponse['salesOrders'];
    purchaseOrders: GetShipmentResponse['purchaseOrders'];
    chargesCount: number;
  };
}
